/**
 * Date utilities for consistent timezone handling across the app.
 * All functions use the user's local timezone to prevent date mismatches.
 */

/**
 * Get today's date in YYYY-MM-DD format using local timezone.
 * This prevents issues where UTC-based dates don't match Fitbit's local-day data.
 */
export function getLocalIsoDate(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getIsoDateInTimezone(dateInput: Date | string | number, timezone: string): string {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const parts = formatter.formatToParts(date);
  const year = parts.find((part) => part.type === 'year')?.value ?? '0000';
  const month = parts.find((part) => part.type === 'month')?.value ?? '01';
  const day = parts.find((part) => part.type === 'day')?.value ?? '01';

  return `${year}-${month}-${day}`;
}

export function getTodayIsoDateInTimezone(timezone: string): string {
  return getIsoDateInTimezone(new Date(), timezone);
}

/**
 * Get the user's timezone identifier (e.g., "Europe/Berlin")
 */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
